/* ============================================================================
   PARHELIK — Recently viewed products (framework-free)

   Ports minimog's recently-viewed-products.js behaviour onto the Parhelik
   recently-viewed section (sections/recently-viewed.liquid).

   Responsibilities
     1. Record the current PDP product id into localStorage (most-recent first,
        de-duplicated, capped). The id is read from [data-current-id] on the
        section root, set server-side only on product templates.
     2. For every [data-ph-recently-viewed] section, read the stored ids, build
        the search URL (?section_id=..&type=product&q=id:N OR id:M), fetch THIS
        section rendered with those results, and inject the returned grid.
     3. After injection, dispatch a scoped shopify:section:load so the global
        engines (parhelik-global.js reveal/swatches, parhelik-product.js
        quick-add/variants) wire the freshly injected cards.

   Re-inits on shopify:section:load (theme editor). No dependencies. Guarded so
   double-loading the asset is harmless.
   ========================================================================== */
(function () {
  'use strict';

  if (window.PHRecentlyViewed && window.PHRecentlyViewed._installed) return;

  var STORAGE_KEY = 'parhelik-recently-viewed';
  var MAX_STORED = 30;
  var ROOT_SELECTOR = '[data-ph-recently-viewed]';

  /* ---------------------------- localStorage ---------------------------- */
  function preferencesAllowed() {
    return Boolean(window.PHPrivacy && window.PHPrivacy.preferencesAllowed());
  }

  function readIds() {
    if (!preferencesAllowed()) return [];
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return [];
      // normalise to integers, drop anything non-numeric
      return parsed
        .map(function (v) { return parseInt(v, 10); })
        .filter(function (v) { return !isNaN(v); });
    } catch (e) {
      return [];
    }
  }

  function writeIds(ids) {
    if (!preferencesAllowed()) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch (e) {
      /* storage unavailable / quota — fail silently */
    }
  }

  // Push current product to the front, de-dupe, cap length.
  function recordId(id) {
    var num = parseInt(id, 10);
    if (isNaN(num)) return;
    var ids = readIds().filter(function (v) { return v !== num; });
    ids.unshift(num);
    if (ids.length > MAX_STORED) ids = ids.slice(0, MAX_STORED);
    writeIds(ids);
  }

  /* ----------------------------- rendering ----------------------------- */
  // Build the "id:N OR id:M" query, excluding the product currently on screen,
  // capped to the section's limit.
  function buildQuery(ids, excludeId, limit) {
    var exclude = parseInt(excludeId, 10);
    var filtered = ids.filter(function (v) { return v !== exclude; });
    if (limit && filtered.length > limit) filtered = filtered.slice(0, limit);
    return filtered
      .map(function (id) { return 'id:' + id; })
      .join(' OR ');
  }

  function hideSection(root) {
    root.setAttribute('hidden', 'hidden');
  }

  function render(root) {
    if (!root) return;
    if (!preferencesAllowed()) { hideSection(root); return; }
    if (root.dataset.phRvLoaded === '1') return;
    root.dataset.phRvLoaded = '1';

    var ids = readIds();
    var currentId = root.getAttribute('data-current-id') || '';
    var limit = parseInt(root.getAttribute('data-limit'), 10) || 8;
    var query = buildQuery(ids, currentId, limit);

    // Nothing to show -> keep the section hidden.
    if (!query) {
      hideSection(root);
      return;
    }

    var base = root.getAttribute('data-fetch-url');
    if (!base) {
      hideSection(root);
      return;
    }
    var url = base + encodeURIComponent(query);

    fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (r) {
        if (!r.ok) throw new Error('recently-viewed http ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (!preferencesAllowed()) { hideSection(root); return; }
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector(ROOT_SELECTOR + ' .ph-recently-viewed__inner');
        var current = root.querySelector('.ph-recently-viewed__inner');
        if (!fresh || !current) {
          hideSection(root);
          return;
        }
        // No product cards came back -> hide the whole section.
        if (!fresh.querySelector('[data-ph-rv-track] .ph-product-card')) {
          hideSection(root);
          return;
        }
        current.innerHTML = fresh.innerHTML;
        root.removeAttribute('hidden');
        rewire(root);
      })
      .catch(function () {
        hideSection(root);
      });
  }

  // Let the global engines wire the injected cards (reveal, swatches, quick-add,
  // variant selection). parhelik-global.js + parhelik-product.js both listen to
  // shopify:section:load and re-scan e.target.
  function rewire(root) {
    if (window.PHProduct && typeof window.PHProduct.init === 'function') {
      try { window.PHProduct.init(root); } catch (e) { /* noop */ }
    }
    // phInternal flag: the global/product engines still wire the injected cards
    // from these events, but OUR OWN section:load listener below skips them so it
    // doesn't reset the loaded flag and re-fetch forever (the flicker loop).
    document.dispatchEvent(
      new CustomEvent('shopify:section:load', { detail: { sectionId: root.id, phInternal: true }, bubbles: false })
    );
    // Some listeners read e.target — emit a second targeted event for them.
    try {
      root.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { phInternal: true }, bubbles: true }));
    } catch (e) {
      /* noop */
    }
  }

  /* ------------------------------- boot -------------------------------- */
  function boot(scope) {
    if (!preferencesAllowed()) return;
    scope = scope || document;
    var roots = scope.querySelectorAll
      ? scope.querySelectorAll(ROOT_SELECTOR)
      : [];

    // Record the current product once (any root on a PDP carries the id).
    for (var i = 0; i < roots.length; i++) {
      var cid = roots[i].getAttribute('data-current-id');
      if (cid) {
        recordId(cid);
        break;
      }
    }

    for (var j = 0; j < roots.length; j++) {
      render(roots[j]);
    }
  }

  function refreshConsent() {
    var allowed = preferencesAllowed();
    document.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      root.dataset.phRvLoaded = '';
      if (!allowed) hideSection(root);
    });
    if (allowed) boot(document);
  }

  function start() {
    if (window.PHPrivacy) window.PHPrivacy.ready.then(refreshConsent);
  }

  document.addEventListener('ph:privacy-change', refreshConsent);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  // Theme editor / re-render: reset the flag and re-fetch for the loaded section.
  document.addEventListener('shopify:section:load', function (e) {
    // Ignore the section:load events WE dispatch after injecting cards — handling
    // them here would reset the loaded flag and re-fetch forever (flicker loop).
    if (e && e.detail && e.detail.phInternal) return;
    var target = (e && e.target && e.target.querySelector) ? e.target : document;
    var root = target.querySelector ? target.querySelector(ROOT_SELECTOR) : null;
    if (target.matches && target.matches(ROOT_SELECTOR)) root = target;
    if (root && root.hasAttribute('data-ph-recently-viewed')) {
      root.dataset.phRvLoaded = '';
      render(root);
    }
  });

  /* ---- public API ---- */
  window.PHRecentlyViewed = {
    _installed: true,
    record: recordId,
    read: readIds,
    refresh: function (root) {
      if (root) { root.dataset.phRvLoaded = ''; render(root); }
      else { boot(document); }
    }
  };
})();
